import type { Pt, Stroke } from './types'

export const COLORS = [
  '#16181D', '#8A8D96', '#C0392B', '#C97B2D', '#E3B23C', '#4C8A6B',
  '#2E8F8A', '#3347E0', '#C46A9A', '#7A5FB8', '#7A4E2D',
]
export const PEN_W = [2.5, 4.5, 7]
export const BRUSH_W = [10, 16, 24]
export const BRUSH_A = [0.45, 0.3, 0.18]

export function hexToRgba(h: string, a: number): string {
  const n = parseInt(h.slice(1), 16)
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`
}

export type ToolState = { color: string; tool: 'pen' | 'brush'; size: 0 | 1 | 2 }

export function strokeStyle(t: ToolState): Pick<Stroke, 'color' | 'w' | 'tool'> {
  return t.tool === 'pen'
    ? { color: t.color, w: PEN_W[t.size], tool: 'pen' }
    : { color: hexToRgba(t.color, BRUSH_A[t.size]), w: BRUSH_W[t.size], tool: 'brush' }
}

type BoardOpts = {
  getTool: () => ToolState
  onChange?: (strokes: Stroke[]) => void
  onStrokeEnd?: (stroke: Stroke) => void
}

/**
 * Холст с телеметрией: каждая точка несёт время, поэтому из штрихов
 * восстанавливаются скорость, паузы и углы. Один и тот же класс работает
 * на всех холстах продукта: «до», практика, свободный, ответ в диалоге.
 */
export class Board {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  strokes: Stroke[] = []
  dpr = Math.min(window.devicePixelRatio || 1, 2)
  private opts: BoardOpts
  private drawing = false
  private cur: Stroke | null = null
  private base: HTMLImageElement | null = null
  private photo: { img: HTMLImageElement; x: number; y: number; w: number; h: number } | null = null

  constructor(canvas: HTMLCanvasElement, opts: BoardOpts) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!
    this.opts = opts
    canvas.addEventListener('pointerdown', this.down)
    canvas.addEventListener('pointermove', this.move)
    canvas.addEventListener('pointerup', this.up)
    canvas.addEventListener('pointercancel', this.up)
  }

  destroy() {
    this.canvas.removeEventListener('pointerdown', this.down)
    this.canvas.removeEventListener('pointermove', this.move)
    this.canvas.removeEventListener('pointerup', this.up)
    this.canvas.removeEventListener('pointercancel', this.up)
  }

  get cssWidth() { return this.canvas.width / this.dpr }
  get cssHeight() { return this.canvas.height / this.dpr }

  resize(w: number, h: number) {
    this.canvas.width = Math.round(w * this.dpr)
    this.canvas.height = Math.round(h * this.dpr)
    this.canvas.style.height = h + 'px'
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    this.redraw()
  }

  /** Подложка практики: PNG рисунка «до». */
  setBase(img: HTMLImageElement | null) {
    this.base = img
    this.redraw()
  }

  setPhoto(img: HTMLImageElement | null) {
    if (!img) { this.photo = null; this.redraw(); return }
    const w = this.cssWidth
    const h = this.cssHeight
    const sc = Math.min(w / img.width, h / img.height)
    const iw = img.width * sc
    const ih = img.height * sc
    this.photo = { img, x: (w - iw) / 2, y: (h - ih) / 2, w: iw, h: ih }
    this.redraw()
  }

  hasPhoto() { return !!this.photo }

  load(strokes: Stroke[]) {
    this.strokes = strokes
    this.redraw()
  }

  undo(): Stroke | undefined {
    const st = this.strokes.pop()
    this.redraw()
    this.opts.onChange?.(this.strokes)
    return st
  }

  clear() {
    this.strokes = []
    this.photo = null
    this.redraw()
    this.opts.onChange?.(this.strokes)
  }

  toDataURL() { return this.canvas.toDataURL('image/png') }

  redraw() {
    const c = this.ctx
    const w = this.cssWidth
    const h = this.cssHeight
    c.fillStyle = '#fff'
    c.fillRect(0, 0, w, h)
    if (this.base?.complete) c.drawImage(this.base, 0, 0, w, h)
    if (this.photo) c.drawImage(this.photo.img, this.photo.x, this.photo.y, this.photo.w, this.photo.h)
    for (const st of this.strokes) this.paint(st)
  }

  private paint(st: Stroke) {
    const c = this.ctx
    c.strokeStyle = st.color
    c.lineWidth = st.w
    c.lineCap = 'round'
    c.lineJoin = 'round'
    c.beginPath()
    st.pts.forEach((p, i) => (i ? c.lineTo(p.x, p.y) : c.moveTo(p.x, p.y)))
    c.stroke()
  }

  private paintLast(st: Stroke) {
    const p = st.pts
    if (p.length < 2) return
    const a = p[p.length - 2]
    const b = p[p.length - 1]
    const c = this.ctx
    c.strokeStyle = st.color
    c.lineWidth = st.w
    c.lineCap = 'round'
    c.lineJoin = 'round'
    c.beginPath()
    c.moveTo(a.x, a.y)
    c.lineTo(b.x, b.y)
    c.stroke()
  }

  private pt(e: PointerEvent): Pt {
    const r = this.canvas.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top, t: performance.now() }
  }

  private down = (e: PointerEvent) => {
    e.preventDefault()
    // захват указателя не критичен: часть браузеров бросает на «чужом» pointerId
    try { this.canvas.setPointerCapture(e.pointerId) } catch { /* рисуем и без захвата */ }
    this.drawing = true
    this.cur = { ...strokeStyle(this.opts.getTool()), pts: [this.pt(e)] }
    this.strokes.push(this.cur)
  }

  private move = (e: PointerEvent) => {
    if (!this.drawing || !this.cur) return
    e.preventDefault()
    this.cur.pts.push(this.pt(e))
    this.paintLast(this.cur)
  }

  private up = () => {
    if (!this.drawing) return
    this.drawing = false
    const st = this.cur
    this.cur = null
    if (!st) return
    // одиночный тап без движения — не штрих
    if (st.pts.length < 2) {
      this.strokes.pop()
      this.redraw()
      return
    }
    this.opts.onStrokeEnd?.(st)
    this.opts.onChange?.(this.strokes)
    this.redraw()
  }
}
